// @ts-nocheck
import React from 'react';
import InteractiveSlider from '../InteractiveSlider';
import { CarComparisonData } from '../../types/carComparison';
import CarSavedScenarios from '../CarSavedScenarios';
import { User } from '../../types/auth';
import { ValidatedInput, useValidation } from '../shared/ValidationSystem';
import { useSharedValidation } from '../../hooks/useSharedValidation';
import { formatters } from '../../utils/sharedUtilities';
import { renderInputSection } from '../../utils/renderInputSection';
import { TylerState } from '../../utils/TylerState';

interface GeneralInputsProps {
  guestScenario?: any;
  onLoadScenarioToA?: (data: CarComparisonData, scenarioName?: string, timestamp?: number) => void;
  onLoadScenarioToB?: (data: CarComparisonData, scenarioName?: string, timestamp?: number) => void;
  onSaveScenario?: (saveRequest: { name: string }) => void;
  user: User | null;
  data: CarComparisonData;
  onInputChange: (field: keyof CarComparisonData, value: any) => void;
  showGuidance: (title: string, content: string) => void;
  comparisonMode?: boolean;
  side?: 'A' | 'B'; // Which side this is for in comparison mode
}

const GeneralInputs: React.FC<GeneralInputsProps> = ({ 
  user,
  data, 
  onInputChange, 
  showGuidance, 
  comparisonMode = false,
  guestScenario,
  onLoadScenarioToA,
  onLoadScenarioToB,
  onSaveScenario,
  side = 'A'
}) => {
  const validation = useSharedValidation('car');
  const renderDetailedMileageInputs = () => {
    const yearsToShow = Math.min(data.ownershipTimeframe, 5);
    
    return (
      <div className="detailed-inputs">
        {Array.from({ length: yearsToShow }, (_, i) => i + 1).map(year => (
          <div key={year} className="detailed-input">
            <label htmlFor={`mileage-year-${year}`}>
              Year {year}
            </label>
            <input
              id={`mileage-year-${year}`}
              type="number"
              min="0"
              step="100"
              value={data[`mileageYear${year}` as keyof CarComparisonData] as number}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                onInputChange(`mileageYear${year}` as keyof CarComparisonData, isNaN(value) ? 0 : Math.max(0, value));
              }}
              className="detailed-number-input"
            />
          </div>
        ))}
        {data.ownershipTimeframe > 5 && (
          <div className="detailed-input">
            <label htmlFor="mileage-year-6-plus">
              Year 6+ 
            </label>
            <input
              id="mileage-year-6-plus"
              type="number"
              min="0"
              step="100"
              value={data.mileageYear6Plus}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                onInputChange('mileageYear6Plus', isNaN(value) ? 0 : Math.max(0, value));
              }}
              className="detailed-number-input"
            />
          </div>
        )}
      </div>
    );
  };

  // Guidance content for help button
  const guidanceContent = 
    'These inputs affect all car ownership scenarios:<br><br>' +
    '<strong>Ownership Timeframe:</strong> How long you plan to keep the car<br>' +
    '<strong>Fuel Efficiency:</strong> Vehicle fuel consumption rate<br>' +
    '<strong>Fuel Cost:</strong> Current fuel prices in your area<br>' +
    '<strong>Annual Mileage:</strong> How much you drive each year<br><br>' +
    'These values are used across all scenarios (buy, finance, lease) to calculate total costs in today\'s dollars.';

  return (
    renderInputSection('General Inputs', (
      <>
        {/* Saved Scenarios */}
        <CarSavedScenarios
          comparisonMode={comparisonMode}
          user={TylerState.user}
          guestScenario={guestScenario}
          currentData={data}
          onLoadScenarioToA={onLoadScenarioToA}
          onLoadScenarioToB={onLoadScenarioToB}
          onSaveScenario={onSaveScenario}
          onInputChange={onInputChange}
          side={side}
        />

        {/* Region Selection */}
        <div className="interactive-slider">
          <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
            <div style={{fontWeight: "bold"}}>Region</div>
            <button
              onClick={() => showGuidance('General Inputs', guidanceContent)}
              className="guidance-btn"
              title="Guidance: General Inputs"
            >
              ℹ
            </button>
          </div>
          <select
            id="region-select"
            value={data.region}
            onChange={(e) => onInputChange('region', e.target.value)}
            className="region-select"
            style={{
              padding: '0.75rem',
              borderRadius: '8px',
              border: '2px solid var(--border)',
              width: '100%',
              fontSize: '1rem'
            }}
          >
            <option value="US">United States</option>
            <option value="Canada">Canada</option>
          </select>
        </div>

        {/* Fuel Type Selection */}
        <div className="interactive-slider">
          <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
            <div style={{fontWeight: "bold"}}>Fuel Type</div>
          </div>
          <select
            id="fuel-type-select"
            value={data.fuelType || 'gasoline'}
            onChange={(e) => onInputChange('fuelType', e.target.value)}
            className="region-select"
            style={{
              padding: '0.75rem',
              borderRadius: '8px',
              border: '2px solid var(--border)',
              width: '100%',
              fontSize: '1rem'
            }}
          >
            <option value="gasoline">Gasoline</option>
            <option value="diesel">Diesel</option>
            <option value="electric">Electric (EV)</option>
            <option value="plugin-hybrid">Plug-in Hybrid</option>
          </select>
        </div>

        <InteractiveSlider
          config={{
            min: 1,
            max: 10,
            step: 1,
            defaultValue: 5,
            format: 'number',
            label: 'Expected Ownership (years)'
          }}
          value={data.ownershipTimeframe}
          onChange={(value) => onInputChange('ownershipTimeframe', value)}
        />

        {/* Fuel Efficiency - changes based on fuel type */}
        {data.fuelType !== 'electric' && (
          <InteractiveSlider
            config={{
              min: data.region === 'Canada' ? 5 : 15,
              max: data.region === 'Canada' ? 25 : 50,
              step: data.region === 'Canada' ? 0.1 : 1,
              defaultValue: data.region === 'Canada' ? 13.1 : 25,
              format: 'number',
              label: data.region === 'Canada' ? 'Fuel Efficiency (L/100 km)' : 'Fuel Efficiency (MPG)'
            }}
            value={data.fuelEfficiency}
            onChange={(value) => onInputChange('fuelEfficiency', value)}
          />
        )}

        {/* Electric Vehicle Efficiency */}
        {(data.fuelType === 'electric' || data.fuelType === 'plugin-hybrid') && (
          <InteractiveSlider
            config={{
              min: 10,
              max: 35,
              step: 0.1,
              defaultValue: 18,
              format: 'number',
              label: 'Efficiency (kWh/100 km)'
            }}
            value={data.kWhPer100km || 18}
            onChange={(value) => onInputChange('kWhPer100km', value)}
          />
        )}

        {/* Fuel Cost - changes based on fuel type */}
        {data.fuelType !== 'electric' && (
          <InteractiveSlider
            config={{
              min: 0,
              max: data.region === 'Canada' ? 3 : 6,
              step: data.region === 'Canada' ? 0.05 : 0.10,
              defaultValue: data.region === 'Canada' ? 1.50 : 3.50,
              format: 'currency',
              label: data.region === 'Canada' ? 'Fuel Cost per Litre ($)' : 'Fuel Cost per Gallon ($)',
              region: data.region
            }}
            value={data.fuelCostPerUnit}
            onChange={(value) => onInputChange('fuelCostPerUnit', value)}
          />
        )}

        {/* Electricity Cost for EVs */}
        {(data.fuelType === 'electric' || data.fuelType === 'plugin-hybrid') && (
          <InteractiveSlider
            config={{
              min: 0.05,
              max: 0.40,
              step: 0.01,
              defaultValue: data.region === 'Canada' ? 0.13 : 0.12,
              format: 'currency',
              label: 'Electricity Cost per kWh ($)',
              region: data.region
            }}
            value={data.costPerKWh || (data.region === 'Canada' ? 0.13 : 0.12)}
            onChange={(value) => onInputChange('costPerKWh', value)}
          />
        )}

        {/* Plug-in Hybrid Usage Pattern */}
        {data.fuelType === 'plugin-hybrid' && (
          <div className="interactive-slider">
            <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
              <div style={{fontWeight: "bold"}}>Electric Driving Mode</div>
            </div>
            <select
              value={data.pluginHybridUsage || 'medium'}
              onChange={(e) => onInputChange('pluginHybridUsage', e.target.value)}
              className="region-select"
              style={{
                padding: '0.75rem',
                borderRadius: '8px',
                border: '2px solid var(--border)',
                width: '100%',
                fontSize: '1rem'
              }}
            >
              <option value="high">High (85% electric, 15% gas)</option>
              <option value="medium">Medium (55% electric, 45% gas)</option>
              <option value="low">Low (25% electric, 75% gas)</option>
            </select>
          </div>
        )}

        {/* Annual Mileage - Simple or Detailed */}
        <div className="interactive-slider">
          <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
            <div style={{fontWeight: "bold"}}>Annual Mileage ({data.region === 'Canada' ? 'km' : 'miles'})</div>
          </div>
          <div className="input-grid">
            <div className="maintenance-input-section">
              <div className="tax-toggle-buttons">
                <button
                  type="button"
                  className={`tax-toggle-btn ${!data.useDetailedMileage ? 'active' : ''}`}
                  onClick={() => onInputChange('useDetailedMileage', false)}
                >
                  Simple
                </button>
                <button
                  type="button"
                  className={`tax-toggle-btn ${data.useDetailedMileage ? 'active' : ''}`}
                  onClick={() => onInputChange('useDetailedMileage', true)}
                >
                  Detailed
                </button>
              </div>
              {!data.useDetailedMileage ? (
                <InteractiveSlider
                  config={{
                    min: 0,
                    max: 50000,
                    step: 500,
                    defaultValue: 12000,
                    format: 'number',
                    label: ''
                  }}
                  value={data.annualMileage}
                  onChange={(value) => onInputChange('annualMileage', value)}
                />
              ) : (
                <div className="detailed-inputs">
                  {renderDetailedMileageInputs()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CO₂ Comparison Toggle */}
        <div className="interactive-slider" style={{background: '#f0fdf4', border: '2px solid #22c55e'}}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}>
            <input
              type="checkbox"
              checked={data.includeCO2Comparison || false}
              onChange={(e) => onInputChange('includeCO2Comparison', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>Include CO₂ Emissions Comparison</div>
              <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                Calculate lifetime CO₂ emissions and environmental cost using Social Cost of Carbon
              </div>
            </div>
          </label>

          {/* Province/State Selector - only show when CO₂ comparison is enabled */}
          {data.includeCO2Comparison && (
            <div style={{ marginTop: '15px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                {data.region === 'Canada' ? 'Province/Territory' : 'State'}
              </div>
              <select
                value={data.province || ''}
                onChange={(e) => onInputChange('province', e.target.value)}
                className="region-select"
                style={{
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '2px solid var(--border)',
                  width: '100%',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select {data.region === 'Canada' ? 'province/territory' : 'state'}</option>
                {data.region === 'Canada' ? (
                  <>
                    <option value="Alberta">Alberta</option>
                    <option value="British Columbia">British Columbia</option>
                    <option value="Manitoba">Manitoba</option>
                    <option value="New Brunswick">New Brunswick</option>
                    <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                    <option value="Northwest Territories">Northwest Territories</option>
                    <option value="Nova Scotia">Nova Scotia</option>
                    <option value="Nunavut">Nunavut</option>
                    <option value="Ontario">Ontario</option>
                    <option value="Prince Edward Island">Prince Edward Island</option>
                    <option value="Quebec">Quebec</option>
                    <option value="Saskatchewan">Saskatchewan</option>
                    <option value="Yukon">Yukon</option>
                  </>
                ) : (
                  <>
                    <option value="Alabama">Alabama</option>
                    <option value="Alaska">Alaska</option>
                    <option value="Arizona">Arizona</option>
                    <option value="Arkansas">Arkansas</option>
                    <option value="California">California</option>
                    <option value="Colorado">Colorado</option>
                    <option value="Connecticut">Connecticut</option>
                    <option value="Delaware">Delaware</option>
                    <option value="Florida">Florida</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Hawaii">Hawaii</option>
                    <option value="Idaho">Idaho</option>
                    <option value="Illinois">Illinois</option>
                    <option value="Indiana">Indiana</option>
                    <option value="Iowa">Iowa</option>
                    <option value="Kansas">Kansas</option>
                    <option value="Kentucky">Kentucky</option>
                    <option value="Louisiana">Louisiana</option>
                    <option value="Maine">Maine</option>
                    <option value="Maryland">Maryland</option>
                    <option value="Massachusetts">Massachusetts</option>
                    <option value="Michigan">Michigan</option>
                    <option value="Minnesota">Minnesota</option>
                    <option value="Mississippi">Mississippi</option>
                    <option value="Missouri">Missouri</option>
                    <option value="Montana">Montana</option>
                    <option value="Nebraska">Nebraska</option>
                    <option value="Nevada">Nevada</option>
                    <option value="New Hampshire">New Hampshire</option>
                    <option value="New Jersey">New Jersey</option>
                    <option value="New Mexico">New Mexico</option>
                    <option value="New York">New York</option>
                    <option value="North Carolina">North Carolina</option>
                    <option value="North Dakota">North Dakota</option>
                    <option value="Ohio">Ohio</option>
                    <option value="Oklahoma">Oklahoma</option>
                    <option value="Oregon">Oregon</option>
                    <option value="Pennsylvania">Pennsylvania</option>
                    <option value="Rhode Island">Rhode Island</option>
                    <option value="South Carolina">South Carolina</option>
                    <option value="South Dakota">South Dakota</option>
                    <option value="Tennessee">Tennessee</option>
                    <option value="Texas">Texas</option>
                    <option value="Utah">Utah</option>
                    <option value="Vermont">Vermont</option>
                    <option value="Virginia">Virginia</option>
                    <option value="Washington">Washington</option>
                    <option value="West Virginia">West Virginia</option>
                    <option value="Wisconsin">Wisconsin</option>
                    <option value="Wyoming">Wyoming</option>
                  </>
                )}
              </select>
              <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>
                Used to calculate CO₂ emissions from electricity generation
              </div>
            </div>
          )}

          {/* EV Rebates - only show for electric/plugin-hybrid */}
          {data.includeCO2Comparison && (data.fuelType === 'electric' || data.fuelType === 'plugin-hybrid') && (
            <>
              <div style={{ marginTop: '15px' }}>
                <InteractiveSlider
                  config={{
                    min: 0,
                    max: 20000,
                    step: 100,
                    defaultValue: 0,
                    format: 'currency',
                    label: 'Purchase Rebate ($)',
                    region: data.region
                  }}
                  value={data.purchaseRebate || 0}
                  onChange={(value) => onInputChange('purchaseRebate', value)}
                />
                <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '-8px', marginBottom: '8px' }}>
                  Government rebate on vehicle purchase (e.g., federal EV incentive)
                </div>
              </div>

              <div style={{ marginTop: '8px' }}>
                <InteractiveSlider
                  config={{
                    min: 0,
                    max: 5000,
                    step: 50,
                    defaultValue: 0,
                    format: 'currency',
                    label: 'Home Charging Installation Cost ($)',
                    region: data.region
                  }}
                  value={data.homeChargingInstallationCost || 0}
                  onChange={(value) => onInputChange('homeChargingInstallationCost', value)}
                />
                <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '-8px', marginBottom: '8px' }}>
                  One-time cost to install Level 2 charger at home
                </div>
              </div>

              <div style={{ marginTop: '8px' }}>
                <InteractiveSlider
                  config={{
                    min: 0,
                    max: 2000,
                    step: 50,
                    defaultValue: 0,
                    format: 'currency',
                    label: 'Charging Installation Rebate ($)',
                    region: data.region
                  }}
                  value={data.chargingRebate || 0}
                  onChange={(value) => onInputChange('chargingRebate', value)}
                />
                <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '-8px' }}>
                  Government/utility rebate for charging installation
                </div>
              </div>
            </>
          )}
        </div>
      </>
    ))
  );
};

export default GeneralInputs;